import {
    KeyedStreamElement,
    KeyedStreamItem,
    StreamElement,
} from 'communication/types/StreamElement';
import { StreamElementType } from 'communication/types/StreamElementType';
import { omitStreamMessageTypeField } from 'utils/messages/StreamMessage';

/*
 * Reduces a keyed stream array
 */
export default function keyedStreamReducer<
    TKeyedStreamElement extends KeyedStreamElement<StreamElementType>,
    TKeyedStreamItem extends KeyedStreamItem,
>(
    element: TKeyedStreamElement,
    state: TKeyedStreamItem[],
    mapToItem: (
        element: TKeyedStreamElement
    ) => TKeyedStreamItem = cloneWithoutTypeDiscriminator
): TKeyedStreamItem[] {
    switch (element._c) {
        case StreamElementType.Add: {
            return add(element, state, mapToItem);
        }
        case StreamElementType.Remove: {
            return state.filter((e) => e._i !== element._i);
        }
        case StreamElementType.Update: {
            return update(element, state, mapToItem);
        }
        case StreamElementType.AddOrUpdate: {
            const existing = !!state.find((i) => i._i === element._i);
            if (existing) {
                return update(element, state, mapToItem);
            } else {
                return add(element, state, mapToItem);
            }
        }
        case StreamElementType.Clear: {
            return [];
        }
        case StreamElementType.Done:
        case StreamElementType.BeginBatch:
        case StreamElementType.EndBatch:
            // Do nothing
            return state;
        default:
            return state;
    }
}

export function cloneWithoutTypeDiscriminator<T extends KeyedStreamItem>(
    element: StreamElement<StreamElementType>
): T {
    return omitStreamMessageTypeField(element) as T;
}

function add<
    TKeyedStreamElement extends KeyedStreamElement<StreamElementType>,
    TKeyedStreamItem extends KeyedStreamItem,
>(
    element: TKeyedStreamElement,
    state: TKeyedStreamItem[],
    mapToItem: (
        element: TKeyedStreamElement
    ) => TKeyedStreamItem = cloneWithoutTypeDiscriminator
) {
    return [...state, mapToItem(element)];
}
function update<
    TKeyedStreamElement extends KeyedStreamElement<StreamElementType>,
    TKeyedStreamItem extends KeyedStreamItem,
>(
    element: TKeyedStreamElement,
    state: TKeyedStreamItem[],
    mapToItem: (
        element: TKeyedStreamElement
    ) => TKeyedStreamItem = cloneWithoutTypeDiscriminator
) {
    return state.map((value) => {
        if (value._i !== element._i) {
            return value;
        }
        return {
            ...value,
            ...mapToItem(element),
        };
    });
}
